import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, InputLabel, List, ListItemButton, ListItemText, MenuItem, Select, Stack,
  Switch, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { ItemChoice, ItemPicker, Loader, NeedsHousehold, PageHeader, useConfirm, useLoad, useNotify } from '../common';
import { SECTIONS, UNITS, parseNumber, searchKey, unitShort, withPendingAlias } from '../labels';
import { AliasEditor, useGoBack } from '../editing';
import { catalogSubtitle } from './CatalogPage';

/** "Bought as it is" when saving an item that was made at home. */
const NONE = '00000000-0000-0000-0000-000000000000';

/** Blank = none; undefined when it is not a number. */
function optional(text: string): number | null | undefined {
  if (!text.trim()) return null;
  const value = parseNumber(text);
  return value === null || value < 0 ? undefined : value;
}

const toInput = (value?: number | null) => (value === null || value === undefined ? '' : String(value));

/** "Ovo" and "Ovos": pick the item this one goes into (similar names first, or search). */
function MergeDialog({ item, catalog, open, onClose, onPick }: {
  item: T.CatalogItem; catalog: T.CatalogItem[]; open: boolean; onClose: () => void; onPick: (keep: T.CatalogItem) => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [choice, setChoice] = useState<ItemChoice | null>(null);
  const others = useMemo(() => catalog.filter(i => i.publicId !== item.publicId), [catalog, item.publicId]);
  const similar = useMemo(() => {
    const key = searchKey(item.name).substring(0, 3);
    return others
      .filter(i => searchKey(i.name).includes(key))
      .sort((a, b) => Math.abs(a.name.length - item.name.length) - Math.abs(b.name.length - item.name.length))
      .slice(0, 8);
  }, [others, item.name]);

  useEffect(() => { if (open) setChoice(null); }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="sm" fullWidth>
      <DialogTitle>Juntar «{item.name}» a…</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Quando é o mesmo artigo com outro nome: tudo passa para o artigo escolhido.
        </Typography>
        {similar.length > 0 && (
          <List dense>
            {similar.map(i => (
              <ListItemButton key={i.publicId} onClick={() => onPick(i)} sx={{ borderRadius: 1 }}>
                <ListItemText primary={i.name} secondary={catalogSubtitle(i)} />
              </ListItemButton>
            ))}
          </List>
        )}
        <Box sx={{ mt: 1 }}>
          <ItemPicker
            items={others}
            allowNew={false}
            label="Procurar outro…"
            value={choice}
            onChange={c => { setChoice(c); if (c && 'item' in c) onPick(c.item); }}
          />
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancelar</Button></DialogActions>
    </Dialog>
  );
}

/** Create (/novo, or /novo?de=<id> for one made at home from that item) or edit (/:id) a catalog item. */
const CatalogItemForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const madeFromId = useSearchParams()[0].get('de');
  const goBack = useGoBack('/casa/catalogo');
  const notify = useNotify();
  const [confirm, confirmDialog] = useConfirm();
  const { data: catalog, error: loadError, loading, reload } = useLoad(() => api.getCatalogItems(), [id]);
  const item = id ? catalog?.find(i => i.publicId === id) : undefined;

  const [name, setName] = useState('');
  const [kind, setKind] = useState<T.CatalogItemKind>('Food');
  const [section, setSection] = useState<T.StoreSection>('Other');
  const [unit, setUnit] = useState<T.MeasureUnit>('Unit');
  const [staple, setStaple] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [min, setMin] = useState('');
  const [reorder, setReorder] = useState('');
  const [pack, setPack] = useState('');
  const [daily, setDaily] = useState('');
  const [aliases, setAliases] = useState<string[]>([]);
  const [preparedFrom, setPreparedFrom] = useState('');
  const [pendingAlias, setPendingAlias] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!item) return;
    setName(item.name);
    setKind(item.kind);
    setSection(item.storeSection);
    setUnit(item.defaultUnit);
    setStaple(item.isPantryStaple);
    setBarcode(item.barcode ?? '');
    setMin(toInput(item.minStock));
    setReorder(toInput(item.reorderQuantity));
    setPack(toInput(item.packSize));
    setDaily(toInput(item.dailyUse));
    setAliases(item.aliases);
    setPreparedFrom(item.preparedFromPublicId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.publicId]);

  // "Criar um artigo feito deste": named after the bought item, counted by the bag, in the freezer section; all editable
  const madeFrom = !id && madeFromId ? catalog?.find(i => i.publicId === madeFromId) : undefined;
  useEffect(() => {
    if (!madeFrom) return;
    setName(`${madeFrom.name} (saco)`);
    setKind('Food');
    setSection('Frozen');
    setUnit('Unit');
    setPreparedFrom(madeFrom.publicId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [madeFrom?.publicId]);

  const run = async (work: () => Promise<unknown>, done: string) => {
    setError(null);
    setBusy(true);
    try {
      await work();
      notify(done, { severity: 'success' });
      goBack();
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  const save = () => {
    if (!name.trim()) { setError('Indica o nome.'); return; }
    const minStock = optional(min);
    const reorderQuantity = optional(reorder);
    const packSize = optional(pack);
    const dailyUse = optional(daily);
    if (minStock === undefined || reorderQuantity === undefined || packSize === undefined || dailyUse === undefined) {
      setError('O mínimo e a quantidade a comprar devem ser números (ou ficar em branco).');
      return;
    }
    const body: T.SaveCatalogItemRequest = {
      name: name.trim(), kind, defaultUnit: unit, storeSection: section, isPantryStaple: staple,
      barcode: barcode.trim() || null, aliases: withPendingAlias(aliases, pendingAlias),
      preparedFromPublicId: preparedFrom || (item ? NONE : null),
      // Blank pack size or daily use clears them (0); an item counted in packages has no pack size
      stockRules: {
        minStock: minStock || null,
        reorderQuantity: reorderQuantity || null,
        packSize: unit === 'Package' ? 0 : packSize ?? 0,
        dailyUse: dailyUse ?? 0,
      },
    };
    run(() => (item ? api.updateCatalogItem(item.publicId, body) : api.createCatalogItem(body)), 'Artigo guardado.');
  };

  const remove = async () => {
    if (!item) return;
    if (!await confirm({ title: 'Apagar', text: `Apagar «${item.name}»?`, confirmText: 'Apagar', danger: true })) return;
    run(() => api.deleteCatalogItem(item.publicId), 'Artigo apagado.');
  };

  const merge = async (keep: T.CatalogItem) => {
    if (!item) return;
    setMerging(false);
    const ok = await confirm({
      title: 'Juntar artigos',
      text: `«${item.name}» passa a ser «${keep.name}»: receitas, lista, idas, preços e stock passam para «${keep.name}» e «${item.name}» fica como outro nome dele.`,
      confirmText: 'Juntar',
    });
    if (ok) run(() => api.mergeCatalogItem(item.publicId, keep.publicId), `Junto a «${keep.name}».`);
  };

  // Bought as they are: an item is not made from a bag, nor from itself
  const sources = (catalog ?? []).filter(i => i.kind === 'Food' && !i.preparedFromPublicId && i.publicId !== item?.publicId);
  const sourceName = sources.find(i => i.publicId === preparedFrom)?.name ?? item?.preparedFromName;
  // The other way round: what is made from this item (then it is bought, not made from another)
  const madeFromThis = item?.preparedItems ?? [];
  const canMakeFromThis = !!item && item.kind === 'Food' && !item.preparedFromPublicId && !preparedFrom;
  const preparedSummary = preparedFrom && sourceName ? `de ${sourceName}`
    : madeFromThis.length > 0 ? `Não · faz-se dele: ${madeFromThis.map(p => p.name).join(', ')}` : 'Não: compra-se assim';

  const moreSummary = [kind === 'Household' ? 'Casa' : 'Alimentar'];
  if (staple) moreSummary.push('básico de despensa');
  if (barcode.trim()) moreSummary.push(`código ${barcode.trim()}`);

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 1 }}>Artigos</Button>
      <PageHeader title={id ? 'Editar artigo' : madeFromId ? 'Novo artigo feito em casa' : 'Novo artigo'} />
      <Loader loading={loading} error={loadError} onRetry={reload} hasData={catalog !== undefined}>
        {id && catalog && !item ? <Alert severity="warning">Este artigo já não existe.</Alert> : (
          <Stack spacing={2} component="form" onSubmit={e => { e.preventDefault(); save(); }}>
            {item?.needsReview && (
              <Alert severity="warning">
                Criado pelo assistente numa contagem. Confirma o nome, a secção e a unidade e carrega em Aprovar, ou junta-o ao artigo que já existe.
              </Alert>
            )}
            <TextField label="Nome" placeholder="Ex.: Detergente da loiça" value={name} onChange={e => setName(e.target.value)} autoFocus={!id} />
            <FormControl>
              <InputLabel id="section-label">Secção do supermercado</InputLabel>
              <Select labelId="section-label" label="Secção do supermercado" value={section} onChange={e => setSection(e.target.value as T.StoreSection)}>
                {SECTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.text}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="unit-label">Unidade habitual</InputLabel>
              <Select labelId="unit-label" label="Unidade habitual" value={unit} onChange={e => setUnit(e.target.value as T.MeasureUnit)}>
                {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.text}</MenuItem>)}
              </Select>
            </FormControl>
            {/* The minimum belongs to the item: the whole house, every place */}
            <TextField
              label={`Mínimo em casa (${unitShort(unit)}, opcional)`}
              value={min}
              onChange={e => setMin(e.target.value)}
              helperText="O total em casa, de todos os sítios. Abaixo disto (contando com as refeições planeadas) vai para a lista de compras."
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField
              label={`Quanto compras de cada vez (${unitShort(unit)}, opcional)`}
              placeholder="Ex.: 12 para os ovos à dúzia"
              value={reorder}
              onChange={e => setReorder(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            {unit !== 'Package' && (
              <TextField
                label={`Quanto traz cada embalagem (${unitShort(unit)}, opcional)`}
                placeholder="Ex.: 6 para um pack de 6"
                value={pack}
                onChange={e => setPack(e.target.value)}
                helperText="Para o que compras em pack (6 pacotes de leite, 1 kg de arroz): a lista pede embalagens inteiras e cada uma que compras soma isto."
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
            )}
            <TextField
              label={`Gasto por dia (${unitShort(unit)}, opcional)`}
              placeholder="Ex.: 1 para 1 L de leite por dia"
              value={daily}
              onChange={e => setDaily(e.target.value)}
              helperText="O que se gasta todos os dias além das refeições (leite, iogurtes, café): sai do stock dia a dia e conta até à próxima ida."
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            {item && item.recipeCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                {item.recipeCount === 1 ? 'Usado em 1 receita.' : `Usado em ${item.recipeCount} receitas.`}
              </Typography>
            )}

            <Box>
              <Accordion disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography>Outros nomes (voz)</Typography>
                    <Typography variant="caption" color="text.secondary">{aliases.length === 0 ? 'Nenhum' : aliases.join(', ')}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Também o reconheço por estes nomes quando falas ou escreves.
                  </Typography>
                  <AliasEditor value={aliases} onChange={setAliases} pending={pendingAlias} onPendingChange={setPendingAlias} />
                </AccordionDetails>
              </Accordion>
              {kind === 'Food' && (
                <Accordion disableGutters variant="outlined" defaultExpanded={!!madeFromId}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography>Faz-se em casa</Typography>
                      <Typography variant="caption" color="text.secondary">{preparedSummary}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {madeFromThis.length === 0 && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Para sacos feitos em casa a partir de um legume comprado (ex.: curgete cortada para a sopa). Cada saco leva um só legume.
                        </Typography>
                        <TextField select fullWidth label="Feito de" value={preparedFrom} onChange={e => setPreparedFrom(e.target.value)}>
                          <MenuItem value="">Nenhum: compra-se assim</MenuItem>
                          {sources.map(i => <MenuItem key={i.publicId} value={i.publicId}>{i.name}</MenuItem>)}
                        </TextField>
                      </>
                    )}
                    {canMakeFromThis && (
                      <Box sx={{ mt: madeFromThis.length === 0 ? 2 : 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {madeFromThis.length > 0
                            ? 'Faz-se em casa a partir deste artigo:'
                            : 'Ou o contrário: fazes em casa outro artigo a partir deste (ex.: sacos de curgete para a sopa)?'}
                        </Typography>
                        {madeFromThis.length > 0 && (
                          <List dense disablePadding sx={{ mb: 1 }}>
                            {madeFromThis.map(p => (
                              <ListItemButton key={p.publicId} onClick={() => navigate(`/casa/catalogo/${p.publicId}`)} sx={{ borderRadius: 1 }}>
                                <ListItemText primary={p.name} />
                              </ListItemButton>
                            ))}
                          </List>
                        )}
                        <Button variant="outlined" onClick={() => navigate(`/casa/catalogo/novo?de=${item!.publicId}`)}>
                          {madeFromThis.length > 0 ? 'Criar outro feito deste' : 'Criar um artigo feito deste'}
                        </Button>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
              <Accordion disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography>Mais opções</Typography>
                    <Typography variant="caption" color="text.secondary">{moreSummary.join(' · ')}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <FormControl>
                      <InputLabel id="kind-label">Tipo</InputLabel>
                      <Select labelId="kind-label" label="Tipo" value={kind} onChange={e => setKind(e.target.value as T.CatalogItemKind)}>
                        <MenuItem value="Food">Alimentar</MenuItem>
                        <MenuItem value="Household">Casa</MenuItem>
                      </Select>
                    </FormControl>
                    <Box>
                      <FormControlLabel
                        control={<Switch checked={staple} onChange={e => setStaple(e.target.checked)} />}
                        label="Básico de despensa"
                      />
                      <Typography variant="caption" color="text.secondary" component="p">
                        Sal, azeite, alho… Não vai para a lista de compras sem confirmares que falta.
                      </Typography>
                    </Box>
                    <TextField
                      label="Código de barras"
                      placeholder="Opcional"
                      value={barcode}
                      onChange={e => setBarcode(e.target.value)}
                      slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Box>

            {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button type="submit" variant="contained" disabled={busy}>{item?.needsReview ? 'Aprovar' : 'Guardar'}</Button>
              {item && <Button onClick={() => setMerging(true)} disabled={busy}>Juntar a outro artigo (é o mesmo)</Button>}
              {item && <Button variant="outlined" color="error" onClick={remove} disabled={busy}>Apagar</Button>}
            </Stack>
          </Stack>
        )}
      </Loader>
      {item && catalog && (
        <MergeDialog item={item} catalog={catalog} open={merging} onClose={() => setMerging(false)} onPick={merge} />
      )}
      {confirmDialog}
    </Box>
  );
};

const CatalogItemEditPage: React.FC = () => <NeedsHousehold><CatalogItemForm /></NeedsHousehold>;

export default CatalogItemEditPage;
